import React, { useMemo } from 'react';

import {
  ArrayField,
  BooleanField,
  ChipField,
  Datagrid,
  DateField,
  Labeled,
  RecordContextProvider,
  SingleFieldList,
  TextField,
  usePermissions,
} from 'react-admin';
import { Alpha3Code } from 'i18n-iso-countries';

import {
  Box,
  Card,
  CardHeader,
  Grid,
  CardContent,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { DateTime } from 'luxon';

import useEntityData from '../../../../hooks/useEntityData';

import PurchaseButton from '../../../layout/button/PurchaseButton';
import NoResults from '../../../layout/NoResults';

import { boldDataGridStyle } from '../../../../constants/style/datagridStyles';
import actions, { Actions } from '../../../../constants/actions';
import { EntityTypes } from '../../../../constants/entityTypes';
import { NationalIdentifierSingleField } from '../../../../customFields/NationalIdentifier';
import LinkField from '../../../layout/LinkField';
import { relatedIndividualsRolesChoices, RelatedIndividualsRoles } from '../../../../constants/relatedIndividualsRoles';
import hasAccess from '../../../../utilities/hasAccess';
import PermissionMissing from '../../../utils/PermissionMissing';

export type BoardMembers = {
  id: string;
  createdAt: string;
  boardMembers: {
    entityId?: string;
    entityType?: EntityTypes;
    firstName?: string;
    surName?: string;
    fullName?: string;
    nationalIdentifier?: {
      country: Alpha3Code;
      identifier: string;
    }[];
    birthDate?: string;
    company: boolean;
    residence?: Alpha3Code;
    citizenship?: Alpha3Code[];
    id?: string;
    roles: {
      fromDate?: string;
      toDate?: string;
      roleCode: string;
      roleName?: string;
    }[];
    type?: string;
  }[];
  errorMessages?: {
    code: string;
    description: string;
  }[];
}

export type Signatories = {
  id: string;
  createdAt: string;
  records: {
    changeDate: string;
    companyId: string;
    companySignatory: string;
    signatories: {
      person?: {
        entityId?: string;
        entityType?: EntityTypes;
        customId?: string;
        firstName?: string;
        lastName?: string;
        birthDate?: string;
        nationalIdentifier?: {
          identifier: string;
          country: Alpha3Code;
        }[];
        citizenship?: Alpha3Code[];
        countryResidence?: Alpha3Code;
      };
      roles: {
        fromDate?: string;
        toDate?: string;
        type: RelatedIndividualsRoles;
        description?: string;
      }[];
      isLimitedAdminSign?: boolean;
    }[];
  }[];
  errorMessages?: {
    code: string;
    description: string;
  }[];
}

const useCompanyRepresentation = () => {
  const {
    data: boardMembersData,
    handlePurchase: handlePurchaseBoardMembers,
    isLoading: isLoadingBoardMembers,
  } = useEntityData<BoardMembers>({
    resource: 'businesses',
    source: 'board-members',
    getAction: actions.BUSINESS_GET_COMPANY_REP,
  });
  const {
    data: signatoryData,
    handlePurchase: handlePurchaseSignatory,
    isLoading: isLoadingSignatory,
  } = useEntityData<Signatories>({
    resource: 'businesses',
    source: 'signatories',
    getAction: actions.BUSINESS_GET_COMPANY_REP,
  });

  return {
    boardMembersData,
    signatoryData,
    handlePurchaseBoardMembers,
    handlePurchaseSignatory,
    isLoading: isLoadingBoardMembers || isLoadingSignatory,
  };
};

const getRoleDisplayValue = (role: {
  fromDate?: string;
  toDate?: string;
  type: RelatedIndividualsRoles;
  description?: string;
}) => {
  const typeDisplayName = relatedIndividualsRolesChoices
    .find((choice) => choice.id === role.type)?.name;
  const description = role.description ?? typeDisplayName ?? 'Unknown';
  return `${description}${role.fromDate ? ` (${DateTime.fromISO(role.fromDate).toISODate()}${role.toDate ? ` -> ${DateTime.fromISO(role.toDate).toISODate()}` : ''})` : ''}`;
};

const CompanyRepresentation = () => {
  const {
    boardMembersData,
    signatoryData,
    handlePurchaseBoardMembers,
    handlePurchaseSignatory,
    isLoading,
  } = useCompanyRepresentation();

  const boardMembers = boardMembersData?.boardMembers?.map((member) => ({
    ...member,
    roles: member?.roles.map((role, index) => ({
      description: getRoleDisplayValue({
        fromDate: role.fromDate,
        toDate: role.toDate,
        type: role.roleCode as RelatedIndividualsRoles,
        description: role.roleName,
      }),
      id: index,
    })),
  }));

  const signatoryRecord = signatoryData?.records.at(0);
  const formattedSignatoryRecord = signatoryRecord ? {
    ...signatoryRecord,
    signatories: signatoryRecord.signatories?.map((signatory) => ({
      ...signatory,
      roles: signatory?.roles.map((role, index) => ({
        description: getRoleDisplayValue(role),
        id: index,
      })),
    })),
  } : signatoryRecord;
  const { permissions } = usePermissions<Actions[]>();
  const hasPermission = useMemo(
    () => hasAccess(permissions, actions.BUSINESS_GET_COMPANY_REP),
    [permissions],
  );

  return (
    <Grid container spacing={6}>
      <Grid size={12}>
        <Card variant="outlined">
          <CardHeader
            title="Board members"
            subheader={(
              hasPermission && (
                <Box display="flex" alignItems="baseline">
                  <Typography>Latest update:</Typography>
                  <DateField
                    record={{
                      createdAt: boardMembersData?.createdAt,
                    }}
                    sx={{ marginLeft: 1, fontWeight: 'bold' }}
                    source="createdAt"
                    emptyText="-"
                    showTime
                  />
                </Box>
              )
            )}
            action={(
              <PurchaseButton
                loading={isLoading}
                label="Get board members"
                onClick={handlePurchaseBoardMembers}
                action={actions.BUSINESS_CREATE_COMPANY_REP}
              />
            )}
          />
          <Divider />
          {boardMembersData?.errorMessages && (boardMembersData?.errorMessages.map((error) => (
            <Grid margin={1} paddingTop={4} container justifyContent="center" key={error.code}>
              <Alert severity="warning">{error.description}</Alert>
            </Grid>
          )))}
          {hasPermission ? (
            <ArrayField record={{ boardMembers }} source="boardMembers">
              <Datagrid sx={boldDataGridStyle} empty={<NoResults />} bulkActionButtons={false}>
                <TextField sortable={false} source="fullName" />
                <TextField sortable={false} source="id" label="ID" />
                <ArrayField sortable={false} source="roles" label="Roles (from date -> to date)">
                  <SingleFieldList linkType={false}>
                    <ChipField source="description" />
                  </SingleFieldList>
                </ArrayField>
              </Datagrid>
            </ArrayField>
          ) : (
            <CardContent>
              <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                <PermissionMissing />
              </Box>
            </CardContent>
          )}
        </Card>
      </Grid>
      <Grid size={12}>
        <Card variant="outlined">
          <CardHeader
            title="Company signatories"
            subheader={(
              hasPermission && (
                <Box display="flex" alignItems="baseline">
                  <Typography>Latest update:</Typography>
                  <DateField
                    record={{
                      createdAt: signatoryData?.createdAt,
                    }}
                    sx={{ marginLeft: 1, fontWeight: 'bold' }}
                    source="createdAt"
                    emptyText="-"
                    showTime
                  />
                </Box>
              )
            )}
            action={(
              <PurchaseButton
                loading={isLoading}
                label="Get signatories"
                onClick={handlePurchaseSignatory}
                action={actions.BUSINESS_CREATE_COMPANY_REP}
              />
            )}
          />
          <Divider />
          {!!signatoryData?.errorMessages?.length && signatoryData.errorMessages.map((error) => (
            <Grid margin={1} paddingTop={4} container justifyContent="center" key={error.code}>
              <Alert severity="warning">{error.description}</Alert>
            </Grid>
          ))}
          {hasPermission && formattedSignatoryRecord && (
            <CardContent>
              <RecordContextProvider value={formattedSignatoryRecord}>
                <Box display="flex" flexDirection="column" gap={4}>
                  <Labeled label="Signing rules">
                    <TextField source="companySignatory" emptyText="-" />
                  </Labeled>
                  <Labeled label="Signatories">
                    <ArrayField source="signatories">
                      <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty="-" resource="individuals">
                        <LinkField source="person.entityId" label="ID" sortable={false} />
                        <TextField source="person.firstName" label="First name" sortable={false} />
                        <TextField source="person.lastName" label="Last name" sortable={false} />
                        <NationalIdentifierSingleField source="person.nationalIdentifier" label="National identifier" sortable={false} />
                        <DateField source="person.birthDate" label="Birth date" sortable={false} />
                        <ArrayField source="roles" label="Roles (from date -> to date)" sortable={false}>
                          <SingleFieldList linkType={false}>
                            <ChipField source="description" />
                          </SingleFieldList>
                        </ArrayField>
                        <BooleanField source="isLimitedAdminSign" sortable={false} label="Has limited signing rights" emptyText="-" />
                      </Datagrid>
                    </ArrayField>
                  </Labeled>
                </Box>
              </RecordContextProvider>
            </CardContent>
          )}
          {hasPermission && !formattedSignatoryRecord && (<NoResults />)}
          {!hasPermission && (
            <CardContent>
              <Box display="flex" alignItems="center" width="100%" flexDirection="column">
                <PermissionMissing />
              </Box>
            </CardContent>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default CompanyRepresentation;
